import { useState } from 'react';
// api
import api from '../utils/api';
import useFlashMessage from './useFlashMessage';

export default function useInvoice(){
    const { setFlashMessage } = useFlashMessage();
    const [summary, setSummary] = useState(null);

    async function createInvoice(token, invoiceData = {}) {
        try {
            const response = await api.post('/invoices/create', invoiceData, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setFlashMessage(
                invoiceData.isReceipt ? 'Recibo criado com sucesso!' : 'Nota criada com sucesso!', 'success'
            );
            return { invoice: response.data.invoice };
        } catch (error) {
            const msg = error.response?.data?.message || 'Erro ao criar nota';
            const upgradeRequired = error.response?.data?.upgradeRequired || false;

            // Só exibe flash de erro se NÃO for limite de plano
            if (!upgradeRequired) {
                setFlashMessage(msg, 'error');
            }

            return {
                error: true,
                message: msg,
                upgradeRequired,
                currentPlan: error.response?.data?.currentPlan
            };
        }
    }

    async function generateReceiptFromInvoice(token, id) {
        try {
            const response = await api.patch(`/invoices/${id}/generate-receipt`, {}, {
                headers: { Authorization: `Bearer ${token}`},
            });
            setFlashMessage('Recibo gerado cm suceso!', 'success');
            return { invoice: response.data.invoice };
        } catch (error) {
            const msg = error.response?.data?.message || 'Erro ao gerar recibo';
            setFlashMessage(msg, 'error');
            return { error: true, message: msg };
        }
    }

    async function revertReceiptToInvoice(token, id) {
        try {
            const response = await api.patch(`/invoices/${id}/revert-receipt`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setFlashMessage('Recibo convertido em orçamento!', 'success');
            return { invoice: response.data.invoice };
        } catch (error) {
            const msg = error.response?.data?.message || 'Erro ao converter em orçamento';
            setFlashMessage(msg, 'error');
            return { error: true, message: msg };
        }
    }

    async function getInvoiceById(token, id){
        let msgText = 'Orçamento buscado pelo id!';
        let msgType = 'success';

        try {
            const response = await api.get(`/invoices/${id}`, {
                headers: { Authorization: `Bearer ${token}`}
            });

            return response.data;
        } catch (error) {
            msgText = error.response.data.message;
            msgType = 'error';
        }
    }

    async function getMyInvoices(token){
        try {
            const response = await api.get('/invoices/myinvoices', {
                headers: { Authorization: `Bearer ${token}` },
            });
            return response.data.invoices || [];;
        } catch (error) {
            const msg = error.response?.data?.message || 'Erro ao buscar notas!';
            setFlashMessage(msg, 'error');
            return [];
        }
    }

    async function getInvoicesByMonth(token, month, year){
        try {
            const response = await api.get('/invoices/filter', {
                headers: { Authorization: `Bearer ${token}` },
                params: { month, year },
            });
            return response.data;
        } catch (error) {
            const msgText = error.response?.data?.message || "Erro ao buscar notas!";
            const msgType = 'error';
            setFlashMessage(msgText, msgType);
            return [];
        }
    }

     /* ── Busca avançada: por ano (sem mês) ── */
    async function getInvoicesByYear(token, year) {
        try {
            const response = await api.get('/invoices/filter-year', {
                headers: { Authorization: `Bearer ${token}` },
                params: { year },
            });
            return response.data;
        } catch (error) {
            if (error.response?.data?.upgradeRequired) {
                return { error: true, upgradeRequired: true, currentPlan: error.response.data.currentPlan };
            }
            const msgText = error.response?.data?.message || "Erro ao buscar notas do ano!";
            setFlashMessage(msgText, 'error');
            return [];
        }
    }
 
    /* ── Busca avançada: todas as notas ── */
    async function getAllInvoices(token) {
        try {
            const response = await api.get('/invoices/all', {
                headers: { Authorization: `Bearer ${token}` },
            });
            return response.data;
        } catch (error) {
            if (error.response?.data?.upgradeRequired) {
                return { error: true, upgradeRequired: true, currentPlan: error.response.data.currentPlan };
            }
            const msgText = error.response?.data?.message || "Erro ao buscar todas as notas!";
            setFlashMessage(msgText, 'error');
            return [];
        }
    }
 
    /* ── Estatísticas por status ── */
    async function getStatistics(token, { scope, year, month } = {}) {
        try {
            const response = await api.get('/invoices/statistics', {
                headers: { Authorization: `Bearer ${token}` },
                params: { scope, year, month },
            });
            return response.data;
        } catch (error) {
            if (error.response?.data?.upgradeRequired) {
                return { error: true, upgradeRequired: true, currentPlan: error.response.data.currentPlan };
            }
            const msgText = error.response?.data?.message || "Erro ao buscar estatísticas!";
            setFlashMessage(msgText, 'error');
            return { error: true, message: msgText };
        }
    }

    async function deleteInvoice(id, token) {
        let msgText = 'Nota deletada com sucesso!';
        let msgType = 'success';

        try {

            const response = await api.delete(`/invoices/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            return response.data;

        } catch (err) {

            msgText = err.response.data.message;
            msgType = 'error';

        }

        setFlashMessage(msgText, msgType);
    }

    async function getSummary(token){
        try {
            const response = await api.get('/invoices/getSummary', {
                headers: { Authorization: `Bearer ${token}` },
            });
            return response.data;
        } catch (error) {
            const msg = error.response?.data?.message || 'Erro ao buscar resumo!';
            setFlashMessage(msg, 'error');
            return null;
        }
    }

    async function fetchSummary(token){
        const data = await getSummary(token);
        if (data) setSummary(data);
    }

    async function updateInvoiceStatus(token, id, status){
        try {
            const response = await api.patch(`/invoices/${id}/status`,
                { status },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            setFlashMessage('Status atualizado com sucesso!', 'success');

            fetchSummary(token);

            return response.data;
        } catch (error) {
            const msgText = error.response?.data?.message || 'Erro ao atualizar status!';
            setFlashMessage(msgText, 'error');

            return { error: true };
        }
    }

    return { createInvoice, generateReceiptFromInvoice, revertReceiptToInvoice, getInvoiceById, getMyInvoices, getInvoicesByMonth, getInvoicesByYear, getAllInvoices, getStatistics, deleteInvoice, updateInvoiceStatus, getSummary, fetchSummary, summary };
}